import React, { useCallback, useContext, useEffect, useState } from "react";

import LoadingButton from "@mui/lab/LoadingButton";
import {
  Alert,
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  TextField,
} from "@mui/material";

import { DEFAULT_REGION_CODE } from "../../constants/index";
import mainStyles from "../../styles";
import {
  getCrowdinProjectLanguages,
  uploadTemplateStringsToCrowdin as uploadTemplateStringsToCrowdinApi,
} from "api/endpoints/seo-templates";
import { callOptionsHeaders } from "api/helpers";
import { DEFAULT_SNACKBAR_DURATION } from "constants/index";
import { SnackbarContext } from "context/snackbar-context";
import { toUUID } from "helpers/s12-helpers";

interface UploadTranslationFormProps {
  getCrowdinFileList: () => void;
}

const UploadTranslationForm = ({
  getCrowdinFileList,
}: UploadTranslationFormProps) => {
  const classes = mainStyles();
  const [isLoading, setIsLoading] = useState(false);
  const [crowdinLanguages, setCrowdinLanguages] = useState<string[]>([]);
  const [formState, setFormState] = useState({
    templateId: "",
    folderName: "",
    languageId: "",
  });
  const [uploadSuccess, setUploadSuccess] = useState(false);

  const { showError } = useContext(SnackbarContext);

  const getCrowdinLanguageList = useCallback(() => {
    setIsLoading(true);
    getCrowdinProjectLanguages(callOptionsHeaders(DEFAULT_REGION_CODE))
      .then((projectLanguages) => {
        setCrowdinLanguages(projectLanguages.languages);
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  }, [showError]);

  const uploadTemplateStringsToCrowdin = () => {
    setIsLoading(true);
    uploadTemplateStringsToCrowdinApi(
      {
        templateId: toUUID(formState.templateId),
        folderName: formState.folderName,
        languageId: formState.languageId,
      },
      callOptionsHeaders(DEFAULT_REGION_CODE, formState.templateId)
    )
      .then(() => {
        setFormState({
          templateId: "",
          folderName: "",
          languageId: "",
        });
        setUploadSuccess(true);
        getCrowdinFileList();
      })
      .catch(showError)
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleTextAreaChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    key: string
  ) => {
    setFormState({
      ...formState,
      [key]: event.target.value,
    });
  };

  const handleSelectChange = (
    event: SelectChangeEvent<string>,
    key: string
  ) => {
    setFormState({
      ...formState,
      [key]: event.target.value,
    });
  };

  useEffect(() => {
    getCrowdinLanguageList();
  }, [getCrowdinLanguageList]);

  return (
    <>
      <Grid item xs={12}>
        <Box
          component="form"
          sx={classes.spaceSearch}
          noValidate
          autoComplete="off"
        >
          <TextField
            id="template-id-input"
            label="Template ID"
            value={formState.templateId}
            onChange={(event) => handleTextAreaChange(event, "templateId")}
            variant="outlined"
            size="small"
            disabled={isLoading}
            inputProps={{ "data-testid": "template-id-input" }}
          />
          <TextField
            id="folder-name-input"
            label="Crowdin Directory"
            value={formState.folderName}
            onChange={(event) => handleTextAreaChange(event, "folderName")}
            variant="outlined"
            size="small"
            disabled={isLoading}
            inputProps={{ "data-testid": "folder-name-input" }}
          />
          <FormControl variant="outlined" size="small" sx={classes.formControl}>
            <InputLabel id="language-select-label">Language</InputLabel>
            <Select
              labelId="language-select-label"
              id="language-select"
              value={formState.languageId}
              label="Language"
              onChange={(event) => handleSelectChange(event, "languageId")}
              inputProps={{ "data-testid": "language-select" }}
            >
              {crowdinLanguages.map((language) => (
                <MenuItem key={language} value={language}>
                  {language}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <LoadingButton
            variant="contained"
            color="primary"
            size="large"
            onClick={() => {
              uploadTemplateStringsToCrowdin();
            }}
            loading={isLoading}
            disabled={
              (formState.templateId &&
                formState.folderName &&
                formState.languageId) == ""
                ? true
                : false
            }
            data-testid="upload-template-button"
          >
            Upload Template
          </LoadingButton>
        </Box>
      </Grid>
      <Snackbar
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        open={uploadSuccess}
        autoHideDuration={DEFAULT_SNACKBAR_DURATION}
        onClose={() => setUploadSuccess(false)}
        data-testid="template-upload-success"
      >
        <Alert severity="success" variant="filled">
          Template Strings Successfully Uploaded to Crowdin
        </Alert>
      </Snackbar>
    </>
  );
};

export default UploadTranslationForm;
